import { useState, useRef, useEffect } from 'react';
import { graphql, readInlineData, FragmentRef, Environment } from 'react-relay';
import { Disposable } from 'relay-runtime';
import { presenceHeartbeat } from './presenceHeartbeatSubscription';
import { getAbTestEligibility } from 'dibs-cookie-jar/exports/abTestHelpers';
import { useIsOnline_viewer } from './__generated__/useIsOnline_viewer.graphql';
import { useIsOnline_seller } from './__generated__/useIsOnline_seller.graphql';
import { useIsOnline_user } from './__generated__/useIsOnline_user.graphql';
import { USER_ONLINE_INTERVAL } from './constants';
import { usePageVisibilityTimeout } from 'dibs-elements/exports/usePageVisibilityTimeout';
import { SUBSCRIPTIONS_TAB_TIMEOUT } from 'dibs-constants/exports/subscriptionsTabTimeout';
import { isMasquerading } from 'dibs-cookie-jar';

const checkUserPresenceEligibility = (presenceRolloutPercentage: string | null): boolean => {
    const rolloutPercent = presenceRolloutPercentage ? parseInt(presenceRolloutPercentage) : 0;

    if (Number.isNaN(rolloutPercent) || !rolloutPercent) {
        return false;
    }

    // rollout to presenceRolloutPercentage of users (via ab cookie)
    const { eligibility } = getAbTestEligibility(document.cookie);
    return parseInt(eligibility) < rolloutPercent;
};

export const checkIsOnline = (recentOnlineDate: string): boolean => {
    if (!recentOnlineDate) {
        return false;
    }
    const lastOnline = new Date(recentOnlineDate);
    const now = new Date();

    return now.valueOf() - lastOnline.valueOf() < USER_ONLINE_INTERVAL;
};

const presenceViewerQuery = graphql`
    fragment useIsOnline_viewer on Viewer @inline {
        presenceFeatureFlag: featureFlag(feature: "presence")
        presenceRolloutPercentage: config(type: "graphQLConfig", key: "presenceRolloutPercentage")
    }
`;

const presenceSellerQuery = graphql`
    fragment useIsOnline_seller on Seller @inline {
        presence {
            recentOnlineDate
        }
    }
`;

const presenceUserQuery = graphql`
    fragment useIsOnline_user on User @inline {
        presence {
            recentOnlineDate
        }
    }
`;

type Props = {
    environment: Environment;
    id: string;
    viewer: FragmentRef<useIsOnline_viewer>;
    user?: FragmentRef<useIsOnline_user>;
    seller?: FragmentRef<useIsOnline_seller>;
};

// Depending on whose online presence we want to monitor,
// useIsOnline takes either a user OR a seller
export const useIsOnline = ({ environment, id, viewer, user, seller }: Props): boolean => {
    const [isOnline, setIsOnline] = useState(false);
    const heartbeatDisposable = useRef<Disposable | null>(null);
    const isVisible = usePageVisibilityTimeout(SUBSCRIPTIONS_TAB_TIMEOUT);
    const [isPresenceEnabled, setIsPresenceEnabled] = useState(false);
    let viewerResponse: useIsOnline_viewer | undefined;
    if (viewer) {
        viewerResponse = readInlineData<useIsOnline_viewer>(presenceViewerQuery, viewer);
    }

    useEffect(() => {
        if (viewerResponse?.presenceFeatureFlag) {
            const isMasked = isMasquerading(document.cookie);
            let isEligible = false;
            if (isMasked) {
                isEligible = true;
            } else if (seller) {
                // if the seller is being queried, presumably this is being called from a buyer
                // app and we need to check if the user has permission to query seller's presence
                isEligible = checkUserPresenceEligibility(viewerResponse.presenceRolloutPercentage);
            } else if (user) {
                // This currently isn't being used to query buyers but if that changes we'll
                // probably need to check against a whitelist or something; that can be done here
                isEligible = false;
            }
            if (isEligible) {
                let recentOnlineDate = null;
                if (seller) {
                    const { presence } = readInlineData<useIsOnline_seller>(
                        presenceSellerQuery,
                        seller
                    );
                    recentOnlineDate = presence?.recentOnlineDate;
                }
                if (user) {
                    const { presence } = readInlineData<useIsOnline_user>(presenceUserQuery, user);
                    recentOnlineDate = presence?.recentOnlineDate;
                }
                setIsPresenceEnabled(true);
                if (recentOnlineDate) {
                    setIsOnline(checkIsOnline(recentOnlineDate));
                }
            }
        }
    }, [seller, user, viewerResponse]);

    const timeout = useRef(0);

    // Subscribe to heartbeats
    useEffect(() => {
        if (isPresenceEnabled) {
            if (isVisible && !heartbeatDisposable.current) {
                const handleHeartbeat = (): void => {
                    setIsOnline(true);
                    window.clearTimeout(timeout.current);
                    timeout.current = window.setTimeout(() => {
                        setIsOnline(false);
                    }, USER_ONLINE_INTERVAL);
                };

                heartbeatDisposable.current = presenceHeartbeat(environment, id, handleHeartbeat);
            } else if (heartbeatDisposable.current && !isVisible) {
                heartbeatDisposable.current.dispose();
                heartbeatDisposable.current = null;
            }
        }
        return () => {
            if (heartbeatDisposable?.current) {
                heartbeatDisposable.current.dispose();
            }
        };
    }, [isOnline, environment, id, isPresenceEnabled, isVisible]);

    return isOnline;
};
